import { Link } from '@tanstack/react-router';
import {
  Button,
  HeroActions,
  HeroBar,
  HeroBody,
  HeroMeta,
  HeroMetaAction,
  HeroMetaSeparator,
  HeroTitle,
  backLinkVariants,
  buttonVariants,
  cn,
  typePillVariants,
} from '@wundo/ui';
import { ChevronLeft, Video } from 'lucide-react';
import type { MeetingPageResponse } from '@wundo/shared/meetingPage.ts';
import { formatDayAndMonth, formatTime } from '@/components/agenda/meetingFormat.ts';
import type { MeetingAttendeeList, MeetingAttendeeRow } from './meetingAttendeeList.ts';
import type { MeetingDialog } from './meetingDialog.ts';
import { meetingPageTitle } from './meetingPageTitle.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingHeaderProps {
  page: MeetingPageResponse;
  attendees: MeetingAttendeeList;
  /* Whether adding the meeting to Wundo is on its way. */
  adding: boolean;
  onOpenDialog: (dialog: MeetingDialog) => void;
  onAddToWundo: () => void;
}

/**
 * The navy bar is the page's title: what the meeting is, and the acts about
 * the meeting as a whole. Under it, the way back to the conversations, the
 * list this page is one entry of.
 */
export function MeetingHeader({
  page,
  attendees,
  adding,
  onOpenDialog,
  onAddToWundo,
}: MeetingHeaderProps) {
  const manual = page.meeting.source === 'manual';

  return (
    <>
      <HeroBar>
        <HeroBody>
          <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1'>
            <HeroTitle>{meetingPageTitle(page)}</HeroTitle>
            {page.journey && <JourneyLabel journey={page.journey} />}
          </div>
          <HeroMeta>
            <span>{whenOf(page)}</span>
            {/* A few names, the organiser marked; past that the line offers the rest. */}
            {attendees.all.length > 0 && (
              <>
                <HeroMetaSeparator />
                <span>{namedAttendees(attendees.named)}</span>
                {attendees.hidden > 0 && (
                  <HeroMetaAction onClick={() => onOpenDialog({ kind: 'attendees' })}>
                    {m.meeting_attendees_more({ count: attendees.hidden })}
                  </HeroMetaAction>
                )}
              </>
            )}
          </HeroMeta>
        </HeroBody>
        <HeroActions>
          {page.meeting.join_url && (
            <a
              className={buttonVariants({ size: 'sm', variant: 'onPrimary' })}
              href={page.meeting.join_url}
              target='_blank'
              rel='noreferrer'
            >
              <Video aria-hidden='true' className='size-3.5' />
              {m.meeting_join()}
            </a>
          )}
          {/* A calendar meeting is changed in the calendar; the server refuses it. */}
          {manual && (
            <>
              <Button
                size='sm'
                variant='onPrimary'
                onClick={() => onOpenDialog({ kind: 'edit' })}
              >
                {m.manual_meeting_edit()}
              </Button>
              <Button
                size='sm'
                variant='onPrimary'
                onClick={() => onOpenDialog({ kind: 'delete' })}
              >
                {m.manual_meeting_delete()}
              </Button>
            </>
          )}
          {/* With Wundo out, there is nothing to set yet: adding is the whole page. */}
          {!manual && !page.decision.follow && (
            <Button
              size='sm'
              variant='onPrimary'
              disabled={adding}
              onClick={onAddToWundo}
            >
              {m.meeting_add_to_wundo()}
            </Button>
          )}
        </HeroActions>
      </HeroBar>

      <Link to='/conversations' className={cn(backLinkVariants(), 'mt-3.5')}>
        <ChevronLeft aria-hidden='true' />
        {m.meeting_back_to_conversations()}
      </Link>
    </>
  );
}

/* The journey step this conversation is: the pink label the agenda gives it. */
function JourneyLabel({
  journey,
}: {
  journey: NonNullable<MeetingPageResponse['journey']>;
}) {
  return (
    <Link
      to='/journeys/$journeyId'
      params={{ journeyId: journey.journey_id }}
      data-slot='meeting-journey-label'
      className={typePillVariants({
        type: 'journey',
        size: 'sm',
        interactive: true,
      })}
    >
      {m.meeting_journey_label({
        name: journey.type_name,
        position: journey.step_position,
        count: journey.step_count,
      })}
    </Link>
  );
}

/** "15 juni · 10:00 – 10:30", in the reader's own locale. */
function whenOf(page: MeetingPageResponse): string {
  const day = formatDayAndMonth(new Date(page.meeting.starts_at));
  return `${day} · ${formatTime(page.meeting.starts_at)} – ${formatTime(page.meeting.ends_at)}`;
}

/** "Toon (organisator), Bea, Chris" — the names the hero line carries. */
function namedAttendees(named: MeetingAttendeeRow[]): string {
  return named
    .map((attendee) =>
      attendee.organizer
        ? m.meeting_attendee_organizer_inline({ name: attendee.label })
        : attendee.label,
    )
    .join(', ');
}
